import { defineStore } from 'pinia';
import { ref, reactive, computed } from 'vue';

export const useDocumentUploadStore = defineStore('documentUpload', () => {
  // Configuration that can be set per component
  const config = ref({
    pollUntilFullyProcessed: false, // Client mode stops at classification
    enableDetailedErrors: true, // Advisor mode shows more error details
    autoChecklistCrossOff: false, // Client mode auto-crosses off checklist
    maxRetries: 30,
    pollingInterval: 5000,
    maxFileSize: 50 * 1024 * 1024, // 50MB
    csrfToken: null,
    isLoading: false,
  });

  // Persistent state - organized by entity to support multiple clients
  const entityDocuments = reactive({}); // entityId -> documents array
  const entityFileProgress = reactive({}); // entityId -> fileName -> progress
  const entityFileStatuses = reactive({}); // entityId -> fileName -> status
  const entityDocumentIdMap = reactive({}); // entityId -> fileName -> documentId
  const entitySessionUploads = ref({}); // entityId -> documents uploaded in current session
  const sessionUploadTrigger = ref(0);
  const activeBatches = reactive({}); // batchId -> polling state
  const uploadStates = reactive({}); // entityId -> upload state

  // Current context
  const currentEntityId = ref(null);
  const isUploading = ref(false);
  const uploadProgress = ref(0);

  // Event callbacks registry
  const eventCallbacks = reactive({});

  // Computed values for current entity
  const currentDocuments = computed(() => {
    if (!currentEntityId.value) return [];
    return entityDocuments[currentEntityId.value] || [];
  });

  const currentFileProgress = computed(() => {
    if (!currentEntityId.value) return {};
    return entityFileProgress[currentEntityId.value] || {};
  });

  const currentFileStatuses = computed(() => {
    if (!currentEntityId.value) return {};
    return entityFileStatuses[currentEntityId.value] || {};
  });

  const currentSessionUploads = computed(() => {
    // This line forces the computed to re-run when trigger changes
    sessionUploadTrigger.value;
    if (!currentEntityId.value) return [];
    return entitySessionUploads.value[currentEntityId.value] || [];
  });

  const hasProcessingDocuments = computed(() => {
    return currentDocuments.value.some(doc =>
      doc.status === 'processing' ||
      doc.status === 'pending' ||
      doc.processing === true,
    );
  });

  const processingCount = computed(() => {
    return currentDocuments.value.filter(doc =>
      doc.status === 'processing' ||
      doc.status === 'pending' ||
      doc.processing === true,
    ).length;
  });

  // Initialize entity state if it doesn't exist
  const initializeEntityState = entityId => {
    if (!entityDocuments[entityId]) {
      entityDocuments[entityId] = [];
    }
    if (!entityFileProgress[entityId]) {
      entityFileProgress[entityId] = {};
    }
    if (!entityFileStatuses[entityId]) {
      entityFileStatuses[entityId] = {};
    }
    if (!entityDocumentIdMap[entityId]) {
      entityDocumentIdMap[entityId] = {};
    }
    if (!entitySessionUploads.value[entityId]) {
      entitySessionUploads.value[entityId] = [];
    }
    if (!uploadStates[entityId]) {
      uploadStates[entityId] = {
        isUploading: false,
        lastUploadTime: null,
      };
    }
  };

  // Set current entity context
  const setCurrentEntity = entityId => {
    currentEntityId.value = entityId;
    initializeEntityState(entityId);
  };

  // Configuration methods
  const updateConfig = newConfig => {
    Object.assign(config.value, newConfig);
  };

  const setMode = mode => {
    if (mode === 'advisor') {
      updateConfig({
        pollUntilFullyProcessed: true,
        enableDetailedErrors: true,
        autoChecklistCrossOff: false,
      });
    } else if (mode === 'client') {
      updateConfig({
        pollUntilFullyProcessed: false,
        enableDetailedErrors: false,
        autoChecklistCrossOff: true,
      });
    }
  };

  // Utility functions
  const isPdfFile = file => {
    return file.type === 'application/pdf';
  };

  const validateFiles = files => {
    const pdfFiles = [];
    const nonPdfFiles = [];

    files.forEach(file => {
      if (isPdfFile(file)) {
        if (file.size > config.value.maxFileSize) {
          throw new Error(`File ${file.name} exceeds maximum size of ${config.value.maxFileSize / (1024 * 1024)}MB`);
        }
        pdfFiles.push(file);
      } else {
        nonPdfFiles.push(file);
      }
    });

    return { pdfFiles, nonPdfFiles };
  };

  const updateFileProgress = (entityId, fileName, progress) => {
    initializeEntityState(entityId);
    entityFileProgress[entityId][fileName] = Math.min(100, Math.round(progress));
    emitEvent('onUploadProgress', { entityId, fileName, progress });
  };

  const updateFileStatus = (entityId, fileName, status) => {
    initializeEntityState(entityId);
    entityFileStatuses[entityId][fileName] = status;
  };

  const initializeDocuments = files => {
    return files.map(file => ({
      name: file.name,
      filename: file.name, // For compatibility with existing code
      classification: null,
      validation: null,
      external_id: null,
      status: 'pending',
      error: null,
      extract_info: [],
      info: 'Processing.',
      processing: true,
      showFullSummary: false,
      uploaded_at: new Date().toISOString(),
    }));
  };

  // Core upload function with progress tracking
  const uploadFilesWithProgress = async (files, entityId, advisor_slug, csrfToken) => {
    const url = '/api/v1/docmaster/documents/upload/';
    const formData = new FormData();
    let totalBytes = 0;

    files.forEach((file, index) => {
      formData.append(`documents[${index}][file]`, file);
      formData.append(`documents[${index}][name]`, file.name);
      totalBytes += file.size;
    });

    formData.append('entity_id', entityId);
    formData.append('advisor_slug', advisor_slug);

    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open('POST', url, true);
      xhr.setRequestHeader('X-CSRFToken', csrfToken);

      xhr.upload.onprogress = event => {
        if (event.lengthComputable) {
          const overallProgress = Math.min(100, Math.round((event.loaded / totalBytes) * 100));
          uploadProgress.value = overallProgress;

          // Update individual file progress proportionally
          files.forEach(file => {
            updateFileProgress(entityId, file.name, overallProgress);
          });
        }
      };

      xhr.onload = () => {
        const response = new Response(xhr.response, {
          status: xhr.status,
          statusText: xhr.statusText,
          headers: new Headers({
            'Content-Type': xhr.getResponseHeader('Content-Type') || 'application/json',
          }),
        });
        resolve(response);
      };

      xhr.onerror = () => {
        reject(new Response(null, { status: xhr.status, statusText: xhr.statusText }));
      };

      xhr.send(formData);
    });
  };

  // Main upload function
  const uploadFiles = async (files, entityId, advisor_slug = null, csrfToken = null) => {
    if (!entityId) {
      throw new Error('Entity ID is required for upload');
    }

    const tokenToUse = csrfToken || config.value.csrfToken;
    if (!tokenToUse) {
      throw new Error('CSRF token is required for upload');
    }

    try {
      setCurrentEntity(entityId);
      isUploading.value = true;
      uploadStates[entityId].isUploading = true;
      emitEvent('onUploadStart', { entityId, files });

      // Validate files
      const { pdfFiles, nonPdfFiles } = validateFiles(files);

      if (nonPdfFiles.length > 0) {
        const fileNames = nonPdfFiles.map(f => f.name).join(', ');
        console.warn(`Non-PDF files ignored: ${fileNames}`);
        emitEvent('onNonPdfFilesIgnored', { entityId, files: nonPdfFiles });
      }

      if (pdfFiles.length === 0) {
        throw new Error('No valid PDF files to upload');
      }

      // Initialize document state
      const newDocuments = initializeDocuments(pdfFiles);
      entityDocuments[entityId].push(...newDocuments);

      // Track session uploads separately
      entitySessionUploads.value[entityId].push(...newDocuments);

      // Initialize file progress tracking
      pdfFiles.forEach(file => {
        updateFileProgress(entityId, file.name, 0);
        updateFileStatus(entityId, file.name, 'Uploading');
      });

      // Upload files
      const response = await uploadFilesWithProgress(pdfFiles, entityId, advisor_slug, tokenToUse);

      if (!response.ok) {
        // Handle batch upload failure
        for (const file of pdfFiles) {
          await handleUploadError(file, response, entityId);
        }
        return;
      }

      const result = await response.json();
      console.log('Upload successful:', result);

      // Update document state with response data
      result.documents.forEach(docData => {
        const doc = entityDocuments[entityId].find(d => d.name === docData.name);
        if (doc) {
          doc.external_id = docData.document_id;
          doc.status = docData.status || 'processing';
          doc.info = 'Processing...';
          entityDocumentIdMap[entityId][doc.name] = docData.document_id;
        }
      });
      // Also update session uploads with external_id
      result.documents.forEach(docData => {
        const sessionDoc = entitySessionUploads.value[entityId]?.find(d => d.name === docData.name);
        if (sessionDoc) {
          sessionDoc.external_id = docData.document_id;
          sessionDoc.status = docData.status || 'processing';
          sessionDoc.info = 'Processing...';
        }
      });

      // Update file progress to 100% and status
      pdfFiles.forEach(file => {
        updateFileProgress(entityId, file.name, 100);
        updateFileStatus(entityId, file.name, 'Processing...');
      });

      uploadStates[entityId].lastUploadTime = new Date().toISOString();
      emitEvent('onUploadComplete', { entityId, result });

      // Start polling for status updates
      if (result.batch_id) {
        startBatchPolling(result.batch_id, entityId);
      }

      return result;
    } catch (error) {
      console.error('Upload error:', error);
      emitEvent('onError', { entityId, error });

      // Update all documents to error state
      files.forEach(file => {
        const doc = entityDocuments[entityId]?.find(d => d.name === file.name);
        if (doc) {
          doc.status = 'failed';
          doc.error = error.message || 'Upload failed';
          doc.processing = false;
        }
        updateFileStatus(entityId, file.name, 'Upload Failed');
      });

      throw error;
    } finally {
      isUploading.value = false;
      uploadStates[entityId].isUploading = false;
    }
  };

  // Error handling for individual files
  const handleUploadError = async (file, response, entityId) => {
    let errorMsg = 'Upload failed';

    try {
      const responseData = await response.clone().json();

      if (response.status === 403) {
        errorMsg = responseData?.error || responseData?.detail || 'You do not have permission to perform this action';
      } else if (response.status === 413) {
        errorMsg = 'File size exceeds limit';
      } else if (response.status === 415) {
        errorMsg = 'Invalid PDF file';
      } else if (responseData?.detail || responseData?.error ) {
        const rawDetail = String(responseData?.detail || responseData?.error);
        const detail = rawDetail.toLowerCase();
        if (detail.includes('encrypted') || detail.includes('password')) {
          errorMsg = 'File is password protected';
        } else {
          console.log('Error upload document to docmaster: ', detail);
        }
      }
    } catch (e) {
      console.error('Failed to parse docmaster upload error response:', e);
      // If we can't parse the response, use generic error
    }

    const doc = entityDocuments[entityId]?.find(d => d.name === file.name);
    if (doc) {
      doc.status = 'failed';
      doc.error = errorMsg;
      doc.processing = false;
    }

    updateFileStatus(entityId, file.name, 'Error');
    emitEvent('onError', { entityId, error: new Error(errorMsg), file });
  };

  // Batch status polling - now continues even when components unmount
  const startBatchPolling = (batchId, entityId) => {
    // Clear any existing polling for this batch
    if (activeBatches[batchId]) {
      clearInterval(activeBatches[batchId].intervalId);
    }

    activeBatches[batchId] = {
      intervalId: null,
      retryCount: 0,
      entityId: entityId,
    };

    const pollBatchStatus = async () => {
      try {
        const response = await fetch(`/api/v1/docmaster/documents/status/${batchId}/`, {
          method: 'GET',
          headers: {
            'X-CSRFToken': config.value.csrfToken,
          },
        });

        const data = await response.json();
        console.log('Batch status:', data);

        let allReady = true;
        const batchDocumentIds = data.documents.map(doc => doc.document_id);

        // Update each document in this batch
        data.documents.forEach(docStatus => {
          const doc = entityDocuments[entityId]?.find(d => d.external_id === docStatus.document_id);
          if (!doc) return;

          updateDocumentFromStatus(doc, docStatus, entityId);

          // Check if document is "ready" based on mode
          const isReady = isDocumentReady(doc, docStatus);
          if (!isReady) {
            allReady = false;
          }
        });

        // Stop polling if all documents are ready or max retries reached
        if (allReady || activeBatches[batchId].retryCount >= config.value.maxRetries) {
          stopBatchPolling(batchId);
          if (allReady) {
            emitEvent('onBatchComplete', { batchId, entityId });
          }
        } else {
          activeBatches[batchId].retryCount++;
        }
      } catch (error) {
        console.error('Polling error:', error);
        activeBatches[batchId].retryCount++;

        if (activeBatches[batchId].retryCount >= 5) {
          stopBatchPolling(batchId);
          emitEvent('onError', { entityId, error });
        }
      }
    };

    // Start polling
    activeBatches[batchId].intervalId = setInterval(pollBatchStatus, config.value.pollingInterval);
    pollBatchStatus(); // Initial call
  };

  const stopBatchPolling = batchId => {
    if (activeBatches[batchId]?.intervalId) {
      clearInterval(activeBatches[batchId].intervalId);
      delete activeBatches[batchId];
    }
  };

  const updateDocumentFromStatus = (doc, docStatus, entityId) => {
    console.log('updateDocumentFromStatus called for:', doc.name, 'entityId:', entityId);
    console.log('Current entitySessionUploads for this entity:', entitySessionUploads.value[entityId]);
    console.log('Looking for external_id:', doc.external_id);
    // Handle errors
    const hasError = docStatus.error &&
                    docStatus.error !== '' &&
                    docStatus.error.trim() !== '[]';

    if (hasError) {
      const errorMsg = parseErrorMessage(docStatus.error);
      doc.error = errorMsg;
      doc.status = 'failed';
      doc.processing = false;
      updateFileStatus(entityId, doc.name, 'Error');
      emitEvent('onError', { entityId, error: new Error(errorMsg), document: doc });
      return;
    }

    // Clear any previous errors
    doc.error = null;

    // Update classification
    const classification = extractClassification(docStatus);
    if (classification && classification !== doc.classification) {
      doc.classification = classification;
      emitEvent('onDocumentClassified', { entityId, document: doc, classification });

      // Auto cross-off checklist if enabled
      if (config.value.autoChecklistCrossOff && doc.external_id) {
        emitEvent('onChecklistCrossOff', { entityId, documentId: doc.external_id, classification });
      }
    }

    // Update summary/info
    if (docStatus.summary) {
      doc.info = docStatus.summary;
    }

    // Update extraction info if available
    if (docStatus.fields) {
      doc.extract_info = docStatus.fields;
    }

    // Update future_model_portfolio if available
    if (docStatus.future_model_portfolio !== undefined) {
      doc.future_model_portfolio = docStatus.future_model_portfolio;
    }

    // Update processing status
    doc.status = docStatus.status || 'processing';

    // Check if document is ready and emit completion event
    if (isDocumentReady(doc, docStatus)) {
      doc.processing = false;
      updateFileStatus(entityId, doc.name, 'Complete');
      emitEvent('onDocumentCompleted', { entityId, document: doc });
    }

    // Also update the corresponding document in session uploads
    console.log('About to sync session uploads');
    console.log('entitySessionUploads keys:', Object.keys(entitySessionUploads));
    console.log('Current entityId:', entityId);
    console.log('Session uploads for current entity:', entitySessionUploads.value[entityId]);

    const sessionDoc = entitySessionUploads.value[entityId]?.find(d => d.external_id === doc.external_id);
    console.log('Found sessionDoc:', sessionDoc);
    console.log('UPDATING SESSION DOC WITH DOC', doc);

    if (sessionDoc) {
      console.log('Updating doc status of a recent upload');
      sessionDoc.error = doc.error;
      sessionDoc.classification = doc.classification;
      sessionDoc.info = doc.info;
      sessionDoc.validation = doc.validation;
      sessionDoc.extract_info = doc.extract_info;
      sessionDoc.status = doc.status;
      sessionDoc.processing = doc.processing;
      // Emit event with the updated document
      emitEvent('onSessionDocumentUpdated', {
        entityId,
        updatedDocument: { ...sessionDoc }, // Send a copy
      });
    } else {
      console.log('No sessionDoc found - checking all session docs:');
      entitySessionUploads.value[entityId]?.forEach((d, index) => {
        console.log(`Session doc ${index}:`, d.name, 'external_id:', d.external_id);
      });
    }
  };

  const isDocumentReady = (doc, docStatus) => {
    if (doc.error) return true; // Errors are "complete"

    if (config.value.pollUntilFullyProcessed) {
      // Advisor mode: wait for full processing
      return docStatus.status === 'completed';
    } else {
      // Client mode: ready when classified
      return doc.classification && doc.classification !== 'N/A';
    }
  };

  const extractClassification = docStatus => {
    if (docStatus.document_type && Array.isArray(docStatus.document_type) && docStatus.document_type.length > 0) {
      const firstDocType = docStatus.document_type[0];
      return firstDocType.document_type || firstDocType.classification;
    }
    return null;
  };

  const parseErrorMessage = error => {
    const lowerErr = error.toLowerCase();

    if (lowerErr.includes('encrypted') || lowerErr.includes('password')) {
      return 'File is password protected or encrypted';
    } else if (lowerErr.includes('not valid')) {
      return 'File is not a valid PDF';
    } else {
      return 'Processing failed - file may have been blocked';
    }
  };

  // Get documents for a specific entity (useful for advisor viewing client docs)
  const getDocumentsForEntity = entityId => {
    return entityDocuments[entityId] || [];
  };

  // Get file progress for a specific entity
  const getFileProgressForEntity = entityId => {
    return entityFileProgress[entityId] || {};
  };

  // Get file statuses for a specific entity
  const getFileStatusesForEntity = entityId => {
    return entityFileStatuses[entityId] || {};
  };

  // Past documents functionality
  const entityPastDocumentsFetched = reactive({}); // entityId -> boolean (track if we've fetched)
  const entityLastFetchTime = reactive({}); // entityId -> timestamp

  // Fetch past documents from server
  const fetchPastDocuments = async (entityId, forceRefresh = false) => {
    if (!entityId) {
      throw new Error('Entity ID is required to fetch past documents');
    }

    // Skip if already fetched recently (unless force refresh)
    const lastFetch = entityLastFetchTime[entityId];
    const fiveMinutesAgo = Date.now() - (5 * 60 * 1000);

    if (!forceRefresh && lastFetch && lastFetch > fiveMinutesAgo) {
      console.log('Past documents already fetched recently, skipping');
      return entityDocuments[entityId] || [];
    }

    try {
      initializeEntityState(entityId);

      let apiUrl; let requestOptions;
      if (config.value.pollUntilFullyProcessed) {
        // Advisor mode - use advisor endpoint
        apiUrl = `/api/v1/advisor-documents/${entityId}`;
        requestOptions = {
          method: 'GET',
          headers: {
            'X-CSRFToken': config.value.csrfToken,
            'Content-Type': 'application/json',
          },
        };
      } else {
        // Client mode - use client endpoint
        apiUrl = `/api/v1/client-portal/documents/${entityId}/`;
        requestOptions = {
          method: 'GET',
          headers: {
            'X-CSRFToken': config.value.csrfToken,
            'Content-Type': 'application/json',
          },
        };
      }

      console.log(`Fetching past documents (mode: ${config.value.pollUntilFullyProcessed ? 'advisor' : 'client'})`);

      const response = await fetch(apiUrl, requestOptions);

      if (!response.ok) {
        throw new Error(`Failed to fetch past documents: ${response.status}`);
      }

      const data = await response.json();
      const pastDocs = data.documents || [];
      console.log(`Fetched ${pastDocs.length} past documents for entity ${entityId}`);

      // Get existing document IDs to avoid duplicates
      const existingIds = new Set(
          (entityDocuments[entityId] || []).map(d => d.external_id).filter(Boolean),
      );

      // Process and add past documents that aren't already in state
      // Using the same mapping logic as DocmasterviewAdvisor
      const processedPastDocs = pastDocs
          .filter(doc => doc.external_id && !existingIds.has(doc.external_id))
          .map(doc => ({
            ...doc, // Keep all original properties
            name: doc.filename || doc.name,
            filename: doc.filename || doc.name,
            external_id: doc.external_id,
            validation: doc.validation || [],
            processing: false, // Past documents are not processing
            showFullSummary: false, // Add this property for compatibility
            showValidationDetails: false, // Add this property for compatibility
            is_past_document: true, // Flag to identify past documents
          }));

      // Add past documents to the beginning of the array (so recent uploads show last)
      if (processedPastDocs.length > 0) {
        entityDocuments[entityId] = [...processedPastDocs, ...(entityDocuments[entityId] || [])];
        console.log(`Added ${processedPastDocs.length} past documents to state`);
      }
      // // REACTIVE assignment: replace the array to ensure Vue 2 detects the change
      // Vue.set(entityDocuments, entityId, processedPastDocs);

      // Mark as fetched and update timestamp
      entityPastDocumentsFetched[entityId] = true;
      entityLastFetchTime[entityId] = Date.now();

      emitEvent('onPastDocumentsFetched', {
        entityId,
        documents: processedPastDocs,
        totalCount: entityDocuments[entityId].length,
      });

      return entityDocuments[entityId];
    } catch (error) {
      console.error('Error fetching past documents:', error);
      emitEvent('onError', { entityId, error });
      throw error;
    }
  };

  // Check if a document is from a past session (vs current upload session)
  const isPastDocument = doc => {
    // Method 1: Explicit flag
    if (doc.is_past_document === true) return true;
    if (doc.is_past_document === false) return false;

    // Method 2: Check if it's currently processing (new uploads are processing initially)
    if (doc.processing === true) return false;

    // Method 3: Check if it has file progress (new uploads have progress tracking)
    const entityId = currentEntityId.value;
    if (entityId && entityFileProgress[entityId] && entityFileProgress[entityId][doc.name]) {
      return false; // Has progress tracking, so it's a recent upload
    }

    // Method 4: Status-based logic - completed docs without recent activity are likely past
    if (doc.status === 'completed' && !doc.processing) {
      return true;
    }

    // Default: assume it's a past document if none of the above conditions met
    return true;
  };

  // Computed properties for current entity's past vs recent documents
  const currentPastDocuments = computed(() => {
    return currentDocuments.value.filter(doc => isPastDocument(doc));
  });

  const currentRecentUploads = computed(() => {
    return currentDocuments.value.filter(doc => !isPastDocument(doc));
  });

  // Get past documents for a specific entity
  const getPastDocumentsForEntity = entityId => {
    const docs = entityDocuments[entityId] || [];
    return docs.filter(doc => isPastDocument(doc));
  };

  // Get recent uploads for a specific entity
  const getRecentUploadsForEntity = entityId => {
    const docs = entityDocuments[entityId] || [];
    return docs.filter(doc => !isPastDocument(doc));
  };


  // Get session uploads for a specific entity (documents uploaded in current session)
  const getSessionUploadsForEntity = entityId => {
    return entitySessionUploads.value[entityId] || [];
  };

  // Clear session uploads (useful when starting fresh)
  const clearSessionUploads = (entityId = null) => {
    if (entityId) {
      entitySessionUploads.value[entityId] = [];
    } else if (currentEntityId.value) {
      entitySessionUploads[currentEntityId.value] = [];
    }
  };

  // Refresh past documents (useful for polling or manual refresh)
  const refreshPastDocuments = async (entityId = null) => {
    const entityToRefresh = entityId || currentEntityId.value;
    if (entityToRefresh) {
      return await fetchPastDocuments(entityToRefresh, true); // Force refresh
    }
  };

  // Auto-fetch past documents when setting current entity
  const setCurrentEntityWithHistory = async (entityId, fetchHistory = true) => {
    setCurrentEntity(entityId);

    if (fetchHistory && !entityPastDocumentsFetched[entityId]) {
      try {
        await fetchPastDocuments(entityId);
      } catch (error) {
        console.warn('Entity ID when failed to fetch past docs', entityId);
        console.warn('Failed to fetch past documents on entity switch:', error);
        // Don't throw - just log the warning and continue
      }
    }
  };

  // Delete a document (for advisor interface)
  const deleteDocument = async (entityId, documentId, csrfToken) => {
    try {
      // Call API to delete document
      const tokenToUse = csrfToken || config.value.csrfToken;
      const response = await fetch(`/api/v1/documents/${documentId}/delete`, {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': tokenToUse,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.status}`);
      }

      // Remove from state (optimistic update - already done on server)
      const docs = entityDocuments[entityId];
      if (!Array.isArray(docs)) {
        console.warn('No cached docs for entity', entityId);
        return true; // server already deleted; nothing to do locally
      }
      const docIndex = entityDocuments[entityId]?.findIndex(d => d.external_id === documentId);
      if (docIndex !== -1) {
        const deletedDoc = entityDocuments[entityId].splice(docIndex, 1)[0];

        // Clean up related state
        if (deletedDoc.name) {
          delete entityFileProgress[entityId]?.[deletedDoc.name];
          delete entityFileStatuses[entityId]?.[deletedDoc.name];
          delete entityDocumentIdMap[entityId]?.[deletedDoc.name];
        }

        emitEvent('onDocumentDeleted', { entityId, documentId, document: deletedDoc });
        console.log(`Deleted document ${documentId} from entity ${entityId}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      emitEvent('onError', { entityId, error });
      throw error;
    }
  };

  // Delete a document AND update the checklist
  const deleteDocumentUpdateChecklist = async (entityId, documentId, csrfToken) => {
    try {
      // Call API to delete document
      const tokenToUse = csrfToken || config.value.csrfToken;

      const response = await fetch(`/api/v1/documents/${documentId}/checklist-and-delete`, {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': tokenToUse,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to delete document: ${response.status}`);
      }

      // Remove from state (optimistic update - already done on server)
      const docIndex = entityDocuments[entityId]?.findIndex(d => d.external_id === documentId);
      if (docIndex !== -1) {
        const deletedDoc = entityDocuments[entityId].splice(docIndex, 1)[0];

        // Clean up related state
        if (deletedDoc.name) {
          delete entityFileProgress[entityId]?.[deletedDoc.name];
          delete entityFileStatuses[entityId]?.[deletedDoc.name];
          delete entityDocumentIdMap[entityId]?.[deletedDoc.name];
        }

        emitEvent('onDocumentDeleted', { entityId, documentId, document: deletedDoc });
        console.log(`Deleted document ${documentId} from entity ${entityId}`);
      }

      return true;
    } catch (error) {
      console.error('Error deleting document:', error);
      emitEvent('onError', { entityId, error });
      throw error;
    }
  };

  // Event system
  const emitEvent = (eventName, data) => {
    const callbacks = eventCallbacks[eventName] || [];
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in event callback ${eventName}:`, error);
      }
    });
  };

  const on = (eventName, callback) => {
    if (!eventCallbacks[eventName]) {
      eventCallbacks[eventName] = [];
    }
    eventCallbacks[eventName].push(callback);

    // Return unsubscribe function
    return () => {
      const index = eventCallbacks[eventName].indexOf(callback);
      if (index > -1) {
        eventCallbacks[eventName].splice(index, 1);
      }
    };
  };

  const off = (eventName, callback = null) => {
    if (!callback) {
      // Remove all callbacks for this event
      eventCallbacks[eventName] = [];
    } else {
      // Remove specific callback
      const callbacks = eventCallbacks[eventName] || [];
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
    }
  };

  // Cleanup function for specific entity or all
  const cleanup = (entityId = null) => {
    if (entityId) {
      // Clean up specific entity
      delete entityDocuments[entityId];
      delete entityFileProgress[entityId];
      delete entityFileStatuses[entityId];
      delete entityDocumentIdMap[entityId];
      delete entitySessionUploads.value[entityId];
      delete uploadStates[entityId];
      delete entityPastDocumentsFetched[entityId];
      delete entityLastFetchTime[entityId];
    } else {
      // Clean up everything
      Object.keys(activeBatches).forEach(stopBatchPolling);
      Object.keys(entityDocuments).forEach(key => delete entityDocuments[key]);
      Object.keys(entityFileProgress).forEach(key => delete entityFileProgress[key]);
      Object.keys(entityFileStatuses).forEach(key => delete entityFileStatuses[key]);
      Object.keys(entityDocumentIdMap).forEach(key => delete entityDocumentIdMap[key]);
      Object.keys(entitySessionUploads).forEach(key => delete entitySessionUploads[key]);
      Object.keys(uploadStates).forEach(key => delete uploadStates[key]);
      Object.keys(entityPastDocumentsFetched).forEach(key => delete entityPastDocumentsFetched[key]);
      Object.keys(entityLastFetchTime).forEach(key => delete entityLastFetchTime[key]);
      Object.keys(eventCallbacks).forEach(key => delete eventCallbacks[key]);
    }
  };

  // Stop all polling (useful when app is closing)
  const stopAllPolling = () => {
    Object.keys(activeBatches).forEach(stopBatchPolling);
  };

  // Resume polling for any active batches (useful when app restarts)
  const resumePolling = () => {
    // This would need to be called with stored batch IDs if you want to resume across sessions
    // For now, polling will restart when new uploads happen
  };

  return {
    // Configuration
    config,
    updateConfig,
    setMode,

    // Current context state
    currentEntityId,
    isUploading,
    uploadProgress,

    // Computed current entity data
    currentDocuments,
    currentFileProgress,
    currentFileStatuses,
    hasProcessingDocuments,
    processingCount,

    // Methods
    setCurrentEntity,
    setCurrentEntityWithHistory,
    uploadFiles,
    updateFileProgress,
    updateFileStatus,
    deleteDocument,
    deleteDocumentUpdateChecklist,

    // Past documents
    fetchPastDocuments,
    refreshPastDocuments,
    isPastDocument,
    currentPastDocuments,
    currentRecentUploads,
    currentSessionUploads,
    entityPastDocumentsFetched,

    // Multi-entity access
    getDocumentsForEntity,
    getFileProgressForEntity,
    getFileStatusesForEntity,
    getPastDocumentsForEntity,
    getRecentUploadsForEntity,
    getSessionUploadsForEntity,
    clearSessionUploads,

    // Event system
    on,
    off,

    // Cleanup
    cleanup,
    stopAllPolling,
    resumePolling,

    // Utilities
    isPdfFile,
    validateFiles,

    // Internal state (for debugging/advanced use)
    entityDocuments,
    activeBatches,
    uploadStates,
  };
});
